import { Alert, Box, Button, TextField, Tooltip } from "@mui/material";
import { Field, useFormikContext } from "formik";
import { type FC, useEffect, useMemo } from "react";
import {
  AutocompleteField,
  CheckboxField,
  MultiAutocompleteField,
  MultiCheckboxField,
  SelectField,
  StringDateRangePickerField,
} from "src/components/UI/Form";
import {
  StagelessAction,
  type WorkPacketAction,
  type WorkPacketActionName,
  WorkPacketFilter,
  WorkPacketFilters,
  type WorkPacketOwner,
} from "src/types/work-packets";
import { WorkPacketType } from "../../WorkPacketType";
import { WorkPacketView } from "../../WorkPacketView";
import { initialFilters, useWorkPacketsContext } from "../../WorkPacketsContext";
import { createStagelessAction, getActionObject } from "../../mappers";
import { useUserNickname } from "../../useUserNickname";
import { useUserOptions } from "../../useUserOptions";
import {
  filterableWorkPacketActions,
  myPacketOwnerViewOptions,
  staticFilterOptions,
} from "../helpers/getStaticFilterOptions";
import { getPacketOwnerLabel } from "./getPacketOwnerLabel";
import { useAuditAccountOptions } from "./hooks/useAuditAccountOptions";
import { useVendorOptions } from "./hooks/userVendorOptions";
import { useAccountOptions } from "./hooks/useAccountOptions";
import { useVcasStatusOptions } from "./hooks/useVcasStatusOptions";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import { FilterFieldGrid } from "../../FilterFieldGrid";
import type { Option } from "components/UI/Form/SelectField";
import { NumberOfCasesFilterValue } from "../helpers/numberOfCases";
import { abandonedReasonOptions } from "src/pages/UserDashboard/Cases/api/constants";
import { useFeatureFlag } from "src/featureFlags/useFeatureFlag";
import { FeatureFlag } from "src/featureFlags/featureFlags";
import { useOptionsWithCurrent } from "src/utils/option-list";

const recoveryStreamOptions = [
  { value: "PPV", title: "PPV" },
  { value: "PQV", title: "PQV" },
];

export const ShortageFiltersForm: FC = () => {
  const { currentFilters } = useWorkPacketsContext();
  const isPreCase = !!currentFilters[WorkPacketFilter.PreCase];
  if (isPreCase) {
    return <PreCaseShortageFilters />;
  } else {
    return <NormalShortageFiltersForm />;
  }
};

const filterCurrentActionOptions = (options: WorkPacketAction[], isReopenFeatureEnabled: boolean) => {
  if (!isReopenFeatureEnabled) {
    return options.filter(option => option.featureFlag !== FeatureFlag.Reopens);
  }
  return options;
};

function useFilteredCurrentActionOptions(isReopenFeatureEnabled: boolean) {
  return useMemo(
    () => filterCurrentActionOptions(filterableWorkPacketActions[WorkPacketType.SHORTAGES], isReopenFeatureEnabled),
    [isReopenFeatureEnabled],
  );
}

const getShortageActionObject = (action: string) =>
  getActionObject(action as WorkPacketActionName, WorkPacketType.SHORTAGES);

const NormalShortageFiltersForm: FC = () => {
  const { setFieldValue, values } = useFormikContext<WorkPacketFilters>();
  const { currentView, currentWorkPacketType } = useWorkPacketsContext();

  const userOptions = useUserOptions();
  const auditAccountOptions = useAuditAccountOptions();
  const accountOptions = useAccountOptions();
  const vendorOptions = useVendorOptions();
  const vcasStatusOptions = useVcasStatusOptions();

  const isReopenFeatureEnabled = useFeatureFlag(FeatureFlag.Reopens);
  const filteredCurrentActionOptions = useFilteredCurrentActionOptions(isReopenFeatureEnabled);
  const currentActionOptions = useOptionsWithCurrent(
    filteredCurrentActionOptions,
    values[WorkPacketFilter.CurrentAction],
    getShortageActionObject,
  );

  const isEligibleReopensFilterEnabled = values[WorkPacketFilter.ShowEligibleReopens];

  return (
    <FilterFieldGrid>
      <Field
        name={WorkPacketFilter.VendorName}
        component={MultiAutocompleteField}
        label="Vendor Name"
        options={vendorOptions}
        setFieldValue={setFieldValue}
      />

      <Field
        name={WorkPacketFilter.StoreName}
        component={MultiAutocompleteField}
        label="Store Name"
        options={accountOptions}
        setFieldValue={setFieldValue}
        disableCommaSeparation
      />

      <Field
        name={WorkPacketFilter.CurrentAction}
        component={AutocompleteField}
        label="Current Action"
        options={currentActionOptions}
        groupBy={(option: any) => option.category}
        getOptionLabel={(option: any) => option.title}
        workPacketType={currentWorkPacketType}
      />

      <Field
        name={WorkPacketFilter.ManualFilingUser}
        component={AutocompleteField}
        label="Manual Filing User"
        options={auditAccountOptions}
        getOptionLabel={(option: any) => option.title}
      />

      {!isEligibleReopensFilterEnabled ? (
        <Field
          name={WorkPacketFilter.RecoveryStream}
          component={AutocompleteField}
          label="Recovery Stream"
          options={recoveryStreamOptions}
          getOptionLabel={(option: any) => option.title}
          placeholder="Recovery Stream"
        />
      ) : (
        <TextField size="small" variant="outlined" label="Recovery Stream" value="PQV" disabled />
      )}

      {currentView === WorkPacketView.AllPackets && (
        <>
          <Field
            name={WorkPacketFilter.CurrentPacketOwner}
            component={AutocompleteField}
            label="Current Packet Owner"
            options={userOptions}
            getOptionLabel={(option: WorkPacketOwner) => option.title}
            placeholder="Current Packet Owner"
          />

          <Field
            name={WorkPacketFilter.BackupPacketOwner}
            component={AutocompleteField}
            label="Backup Packet Owner"
            options={userOptions}
            getOptionLabel={(option: WorkPacketOwner) => option.title}
            placeholder="Backup Packet Owner"
            setFieldValue={setFieldValue}
          />
        </>
      )}

      <Field
        name={WorkPacketFilter.ParentInvoiceId}
        component={MultiAutocompleteField}
        options={EMPTY_ARRAY}
        label="Parent Invoice ID"
        setFieldValue={setFieldValue}
        disableSuggestions
      />

      <Field
        name={WorkPacketFilter.DisputedInvoiceId}
        component={MultiAutocompleteField}
        options={EMPTY_ARRAY}
        label="Disputed Invoice ID"
        disableSuggestions
        setFieldValue={setFieldValue}
      />

      <Field
        name={WorkPacketFilter.DisputeId}
        component={MultiAutocompleteField}
        options={EMPTY_ARRAY}
        label="Dispute ID"
        disableSuggestions
        setFieldValue={setFieldValue}
      />

      <Field name={WorkPacketFilter.InvoiceDueDate} component={StringDateRangePickerField} label="Invoice Due Date" />

      <Box display="none" flexDirection="row" alignItems="center">
        <Box flexGrow={1}>
          <Field
            name={WorkPacketFilter.DisputeCreatedAt}
            component={StringDateRangePickerField}
            label="Dispute - Created At"
          />
        </Box>
        <Field name={WorkPacketFilter.RedFlags} component={CheckboxField} label="Red flags" />
      </Box>

      <Field
        name={WorkPacketFilter.PaymentStatus}
        component={MultiCheckboxField}
        label="Payment Status"
        options={staticFilterOptions[WorkPacketFilter.PaymentStatus]}
        setFieldValue={setFieldValue}
        disableSuggestions
      />

      <Field
        name={WorkPacketFilter.VcasDisputeStatus}
        component={MultiAutocompleteField}
        label="VCAS Dispute Status"
        options={vcasStatusOptions}
        setFieldValue={setFieldValue}
      />

      {currentView === WorkPacketView.MyPackets && (
        <Field
          name={WorkPacketFilter.MyPacketsView}
          component={SelectField}
          label="Show me packets where I am a..."
          options={myPacketOwnerViewOptions[WorkPacketType.SHORTAGES]}
          getOptionLabel={(option: Option) => option.value}
          setFieldValue={setFieldValue}
        />
      )}

      <Field name={WorkPacketFilter.ShowExpired} component={CheckboxField} label="Include expired" />
      <Field name={WorkPacketFilter.ShowMigrated} component={CheckboxField} label="Include migrated" />

      {isReopenFeatureEnabled && (
        <Field name={WorkPacketFilter.ShowEligibleReopens} component={CheckboxField} label="Include eligible reopens" />
      )}
    </FilterFieldGrid>
  );
};

const preCaseActions = [
  createStagelessAction(StagelessAction.ReadyForReview, WorkPacketType.SHORTAGES),
  createStagelessAction(StagelessAction.NotPursuing, WorkPacketType.SHORTAGES),
  createStagelessAction(StagelessAction.Investigate, WorkPacketType.SHORTAGES),
  createStagelessAction(StagelessAction.Abandoned, WorkPacketType.SHORTAGES),
];

const PreCaseShortageFilters: FC = () => {
  const { currentWorkPacketType, currentView } = useWorkPacketsContext();
  const userOptions = useUserOptions();
  const vendorOptions = useVendorOptions();
  const auditAccountOptions = useAuditAccountOptions();
  const userNickname = useUserNickname();
  const accountOptions = useAccountOptions();
  const { setFieldValue, values, initialValues, submitForm } = useFormikContext<WorkPacketFilters>();
  const hasNonDefaultNumberOfEscalationsFilter =
    initialValues[WorkPacketFilter.NumberOfEscalations] !== initialFilters[WorkPacketFilter.NumberOfEscalations];

  useEffect(() => {
    if (
      !(
        values[WorkPacketFilter.PreviouslyRemovedFromEscalations] ||
        values[WorkPacketFilter.PreviouslyRemovedFromSettlements]
      )
    ) {
      setFieldValue(WorkPacketFilter.CaseID, "");
    }
  }, [setFieldValue, values]);

  return (
    <>
      <FilterFieldGrid>
        <Field type="hidden" name={WorkPacketFilter.PreCase} value={true} />
        <Field
          name={WorkPacketFilter.VendorName}
          component={MultiAutocompleteField}
          label="Vendor Name"
          options={vendorOptions}
          setFieldValue={setFieldValue}
        />

        <Field
          name={WorkPacketFilter.StoreName}
          component={MultiAutocompleteField}
          label="Store Name"
          options={accountOptions}
          setFieldValue={setFieldValue}
          disableCommaSeparation
        />

        <Field
          name={WorkPacketFilter.PayeeCode}
          component={MultiAutocompleteField}
          options={EMPTY_ARRAY}
          label="Payee Code"
          disableSuggestions
          setFieldValue={setFieldValue}
        />

        <Field
          name={WorkPacketFilter.VendorCode}
          component={MultiAutocompleteField}
          options={EMPTY_ARRAY}
          label="Vendor Code"
          disableSuggestions
          setFieldValue={setFieldValue}
        />

        <Field
          name={WorkPacketFilter.CurrentAction}
          component={AutocompleteField}
          label="Current Action"
          options={preCaseActions}
          groupBy={(option: any) => option.category}
          getOptionLabel={(option: any) => option.title}
          placeholder="Current Action"
          workPacketType={currentWorkPacketType}
        />

        <Field
          name={WorkPacketFilter.ManualFilingUser}
          component={AutocompleteField}
          label="Manual Filing User"
          options={auditAccountOptions}
          getOptionLabel={(option: any) => option.title}
        />

        <Field
          name={WorkPacketFilter.CurrentPacketOwner}
          component={AutocompleteField}
          label={getPacketOwnerLabel(currentView, userNickname)}
          options={userOptions}
          getOptionLabel={(option: WorkPacketOwner) => option.title}
          placeholder="Current Packet Owner"
          disabled={currentView !== WorkPacketView.AllPackets}
        />

        <Field
          name={WorkPacketFilter.BackupPacketOwner}
          component={AutocompleteField}
          label="Backup Packet Owner"
          options={userOptions}
          getOptionLabel={(option: WorkPacketOwner) => option.title}
          placeholder="Backup Packet Owner"
          disabled={currentView !== WorkPacketView.AllPackets}
        />

        <Field
          name={WorkPacketFilter.DisputeCreatedAt}
          component={StringDateRangePickerField}
          label="Dispute - Created At"
        />

        <Field
          name={WorkPacketFilter.ParentInvoiceId}
          component={MultiAutocompleteField}
          options={EMPTY_ARRAY}
          label="Parent Invoice ID"
          setFieldValue={setFieldValue}
          disableSuggestions
        />

        <Field
          name={WorkPacketFilter.DisputedInvoiceId}
          component={MultiAutocompleteField}
          options={EMPTY_ARRAY}
          label="Disputed Invoice ID"
          disableSuggestions
          setFieldValue={setFieldValue}
        />

        <Field name={WorkPacketFilter.InvoiceDueDate} component={StringDateRangePickerField} label="Invoice Due Date" />

        <Field
          name={WorkPacketFilter.NumberOfEscalations}
          component={AutocompleteField}
          label="# Of Escalations"
          options={staticFilterOptions[WorkPacketFilter.NumberOfEscalations]}
          getOptionLabel={(option: any) => option.title}
          disableClearable
          sx={
            hasNonDefaultNumberOfEscalationsFilter ? { outline: `4px solid #ffe7c6`, borderRadius: "4px" } : undefined
          }
        />

        <Field
          name={WorkPacketFilter.PreviouslyRemovedFromEscalations}
          component={CheckboxField}
          label="Previously removed from Escalations"
        />

        <Field
          name={WorkPacketFilter.NumberOfSettlements}
          component={AutocompleteField}
          label="# Of Settlements"
          options={staticFilterOptions[WorkPacketFilter.NumberOfSettlements]}
          getOptionLabel={(option: any) => option.title}
        />

        <Field
          name={WorkPacketFilter.PreviouslyRemovedFromSettlements}
          component={CheckboxField}
          label="Previously removed from Settlements"
        />
        <Tooltip title="Select either Previously removed from Escalations or Previously removed from Settlements to enable this field">
          <Field
            name={WorkPacketFilter.CaseID}
            disabled={
              !(
                (values[WorkPacketFilter.PreviouslyRemovedFromEscalations] &&
                  !values[WorkPacketFilter.PreviouslyRemovedFromSettlements]) ||
                (values[WorkPacketFilter.PreviouslyRemovedFromSettlements] &&
                  !values[WorkPacketFilter.PreviouslyRemovedFromEscalations])
              )
            }
            label={
              values[WorkPacketFilter.PreviouslyRemovedFromEscalations] &&
              values[WorkPacketFilter.PreviouslyRemovedFromSettlements]
                ? "Amazon Case ID (filter unavailable)"
                : values[WorkPacketFilter.PreviouslyRemovedFromEscalations]
                  ? "Escalation Amazon Case ID"
                  : values[WorkPacketFilter.PreviouslyRemovedFromSettlements]
                    ? "Settlement Amazon Case ID"
                    : "Amazon Case ID (filter not selected)"
            }
            as={TextField}
            size="small"
            variant="outlined"
          />
        </Tooltip>

        <Field
          name={WorkPacketFilter.PaymentStatus}
          component={MultiCheckboxField}
          label="Payment Status"
          options={staticFilterOptions[WorkPacketFilter.PaymentStatus]}
          setFieldValue={setFieldValue}
          disableSuggestions
        />

        <Field name={WorkPacketFilter.ShowExpired} component={CheckboxField} label="Include expired" />

        <Field name={WorkPacketFilter.ShowMigrated} component={CheckboxField} label="Include migrated" />

        <Field
          name={WorkPacketFilter.AbandonedReason}
          component={AutocompleteField}
          label="Abandoned Reason"
          options={abandonedReasonOptions}
          getOptionLabel={(option: any) => option.title}
          placeholder="Abandoned Reason"
          setFieldValue={setFieldValue}
        />
      </FilterFieldGrid>
      {hasNonDefaultNumberOfEscalationsFilter && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <p style={{ marginBottom: "1rem" }}>
            {initialValues[WorkPacketFilter.NumberOfEscalations] === NumberOfCasesFilterValue.MORE_THAN_ZERO ? (
              <>
                <b>You are viewing work packets that have already been re-disputed once.</b> The number of escalations
                filter is set to "More than zero".
              </>
            ) : (
              <>
                <b>You are viewing work packets that have potentially already been re-disputed once.</b> The number of
                escalations filter is set to "All".
              </>
            )}
          </p>
          <Button
            variant="outlined"
            color="primary"
            sx={{ py: 0.5 }}
            onClick={() => {
              setFieldValue(WorkPacketFilter.NumberOfEscalations, initialFilters[WorkPacketFilter.NumberOfEscalations]);
              submitForm();
            }}
          >
            Only show work packets that have not yet been re-disputed
          </Button>
        </Alert>
      )}
    </>
  );
};
